<template>
    <div class="actions-editor">
        <div v-if="localActions.length === 0" class="empty-actions">
            <div class="empty-icon">
                <i class="material-icons">playlist_add</i>
            </div>
            <p>{{ $t("contactModuleRules.actionsEditor.noActions") }}</p>
            <a-button type="primary" @click="addAction">
                <i class="material-icons">add</i>
                {{ $t("contactModuleRules.actionsEditor.addFirstAction") }}
            </a-button>
        </div>

        <div v-else>
            <!-- Actions List -->
            <draggable
                v-model="localActions"
                handle=".action-drag-handle"
                class="actions-list"
            >
                <div
                    v-for="(action, index) in localActions"
                    :key="`action-${index}`"
                    class="action-card"
                >
                    <div class="action-header">
                        <i class="material-icons action-drag-handle">drag_indicator</i>
                        <span class="action-number">{{ $t("contactModuleRules.actionsEditor.actionNumber", { n: index + 1 }) }}</span>
                        <div class="spacer"></div>
                        <a-button
                            type="text"
                            size="small"
                            danger
                            @click="removeAction(index)"
                        >
                            <i class="material-icons">delete</i>
                        </a-button>
                    </div>

                    <div class="action-body">
                        <!-- Action Type Selector -->
                        <div class="action-type-selector">
                            <label>{{ $t("contactModuleRules.actionsEditor.actionType") }}</label>
                            <a-select
                                v-model="action.type"
                                @change="() => onActionTypeChange(action)"
                                style="width: 100%;"
                            >
                                <a-select-option value="none">
                                    <i class="material-icons">block</i>
                                    {{ $t("contactModuleRules.doNothing") }}
                                </a-select-option>
                                <a-select-option value="change-owner">
                                    <i class="material-icons">assignment_ind</i>
                                    {{ $t("contactModuleRules.changeOwner") }}
                                </a-select-option>
                                <a-select-option value="add-tag">
                                    <i class="material-icons">label</i>
                                    {{ $t("contactModuleRules.addTag") }}
                                </a-select-option>
                                <a-select-option value="add-department">
                                    <i class="material-icons">business</i>
                                    {{ $t("contactModuleRules.addDepartment") }}
                                </a-select-option>
                                <a-select-option value="upsert-contact-property">
                                    <i class="material-icons">edit_note</i>
                                    {{ $t("contactModuleRules.updateProperty") }}
                                </a-select-option>
                            </a-select>
                        </div>

                        <!-- Action Configuration -->
                        <div v-if="action.type !== 'none'" class="action-config">
                            <!-- Change Owner Config -->
                            <ChangeOwnerConfig
                                v-if="action.type === 'change-owner'"
                                v-model="action.changeOwner"
                                @input="emitChange"
                            />

                            <!-- Add Tag Config -->
                            <AddTagConfig
                                v-if="action.type === 'add-tag'"
                                v-model="action.addTag"
                                @input="emitChange"
                            />

                            <!-- Add Department Config -->
                            <AddDepartmentConfig
                                v-if="action.type === 'add-department'"
                                v-model="action.addDepartment"
                                :when-type="whenType"
                                @input="emitChange"
                            />

                            <!-- Upsert Contact Property Config -->
                            <UpsertContactPropertyConfig
                                v-if="action.type === 'upsert-contact-property'"
                                v-model="action.upsertContactProperty"
                                @input="emitChange"
                            />
                        </div>
                    </div>
                </div>
            </draggable>

            <!-- Add Action Button -->
            <div class="add-action-btn" @click="addAction">
                <i class="material-icons">add</i>
                <span>{{ $t("contactModuleRules.actionsEditor.addAction") }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import ChangeOwnerConfig from "./ChangeOwnerConfig.vue";
import AddTagConfig from "./AddTagConfig.vue";
import AddDepartmentConfig from "./AddDepartmentConfig.vue";
import UpsertContactPropertyConfig from "./UpsertContactPropertyConfig.vue";
import log2 from "../../../../../../utils/log2";

const log = log2("ActionsEditor");

export default {
    name: "ActionsEditor",
    components: {
        draggable,
        ChangeOwnerConfig,
        AddTagConfig,
        AddDepartmentConfig,
        UpsertContactPropertyConfig
    },
    props: {
        value: {
            type: Array,
            required: true
        },
        whenType: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            localActions: []
        };
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.localActions)) {
                    this.localActions = JSON.parse(JSON.stringify(newVal));
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        emitChange() {
            this.$emit("input", JSON.parse(JSON.stringify(this.localActions)));
        },

        addAction() {
            this.localActions.push({
                type: "none",
                changeOwner: this.getDefaultChangeOwnerConfig(),
                addTag: { tagIds: [] },
                addDepartment: { departmentId: null, applyToConversation: false },
                upsertContactProperty: { propertyId: null, value: "" }
            });
            this.emitChange();
        },

        removeAction(index) {
            this.localActions.splice(index, 1);
            this.emitChange();
        },

        onActionTypeChange(action) {
            // Reset configs when type changes
            if (action.type === 'change-owner' && !action.changeOwner) {
                action.changeOwner = this.getDefaultChangeOwnerConfig();
            }
            if (action.type === 'add-tag' && !action.addTag) {
                action.addTag = { tagIds: [] };
            }
            if (action.type === 'add-department' && !action.addDepartment) {
                action.addDepartment = { departmentId: null, applyToConversation: false };
            }
            if (action.type === 'upsert-contact-property' && !action.upsertContactProperty) {
                action.upsertContactProperty = { propertyId: null, value: "" };
            }
            this.emitChange();
        },

        getDefaultChangeOwnerConfig() {
            return {
                method: "static",
                staticUserId: null,
                userList: [],
                useVisibilityControl: false,
                syncToZoho: false,
                algorithmSettings: {
                    algorithm: "least_contact",
                    dateRange: "today",
                    checkOnlineStatus: false
                }
            };
        }
    }
};
</script>

<style lang="scss" scoped>
.actions-editor {
    .empty-actions {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        background: #fafafa;
        border: 2px dashed #d9d9d9;
        border-radius: 12px;

        .empty-icon {
            .material-icons {
                font-size: 64px;
                color: #d9d9d9;
            }
        }

        p {
            color: #999;
            margin: 16px 0 24px 0;
            font-size: 14px;
        }

        button {
            .material-icons {
                font-size: 18px;
                margin-right: 4px;
            }
        }
    }

    .actions-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 16px;

        .action-card {
            background: #ffffff;
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            padding: 16px;

            .action-header {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 16px;
                padding-bottom: 12px;
                border-bottom: 1px solid #f0f0f0;

                .action-drag-handle {
                    cursor: grab;
                    color: #999;
                    font-size: 20px;

                    &:active {
                        cursor: grabbing;
                    }
                }

                .action-number {
                    font-weight: 600;
                    color: #1a1a1a;
                }

                .spacer {
                    flex: 1;
                }

                button {
                    .material-icons {
                        font-size: 16px;
                    }
                }
            }

            .action-body {
                .action-type-selector {
                    margin-bottom: 16px;

                    label {
                        display: block;
                        font-weight: 600;
                        margin-bottom: 8px;
                        color: #1a1a1a;
                    }

                    .ant-select-selection {
                        .material-icons {
                            font-size: 16px;
                            vertical-align: middle;
                            margin-right: 6px;
                        }
                    }
                }

                .action-config {
                    padding: 16px;
                    background: #fafafa;
                    border-radius: 6px;
                }
            }
        }
    }

    .add-action-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 16px;
        border: 2px dashed #1890ff;
        border-radius: 8px;
        cursor: pointer;
        color: #1890ff;
        font-weight: 600;
        transition: all 0.3s ease;

        &:hover {
            background: #e6f7ff;
        }

        .material-icons {
            font-size: 20px;
        }
    }
}
</style>

